/**
 * 单个 Editor 会话内的 WorkAssembly 不可变版本注册表。
 *
 * 注册表同时封装已完成快照和正在构建的事务，保证相同版本不会重复读取归档、
 * 解压或实例化 GLB。网络与解析细节由调用方注入的 builder 负责，cad-core
 * 只管理版本语义、原子发布和资源生命周期。
 */
export class WorkAssemblyVersionRegistry {
  /** 已成功发布的不可变版本快照。 */
  private readonly completedSnapshots = new Map<string, PuppyCAD.WorkAssemblySnapshot>()

  /** 正在构建的版本事务，用于合并同版本并发请求。 */
  private readonly inFlightBuilds = new Map<string, Promise<PuppyCAD.WorkAssemblySnapshot>>()

  /** 注册表是否已经随 Editor 会话销毁。 */
  private disposed = false

  /**
   * 查询已经成功发布的版本快照。
   *
   * @param versionKey 需要查询的仓库与提交版本。
   * @returns 命中时返回完整快照，未命中时返回 null。
   */
  public get(versionKey: PuppyCAD.WorkAssemblyVersionKey): PuppyCAD.WorkAssemblySnapshot | null {
    this.assertAvailable()
    const normalizedVersionKey = this.normalizeVersionKey(versionKey)
    return this.completedSnapshots.get(this.createCacheKey(normalizedVersionKey)) ?? null
  }

  /**
   * 解析或构建一个完整 WorkAssembly 版本。
   *
   * 已完成版本直接复用；正在构建的相同版本共享同一个事务；只有真正未命中时才调用
   * builder。builder 抛错时不会发布任何快照，调用方可以在下一次请求中重新构建。
   *
   * @param versionKey 目标仓库与提交版本。
   * @param builder 缓存未命中时执行的完整加载事务。
   * @returns 已发布或新构建的完整版本快照。
   */
  public resolve(
    versionKey: PuppyCAD.WorkAssemblyVersionKey,
    builder: PuppyCAD.WorkAssemblyBuilder,
  ): Promise<PuppyCAD.WorkAssemblySnapshot> {
    try {
      this.assertAvailable()
      const normalizedVersionKey = this.normalizeVersionKey(versionKey)
      const cacheKey = this.createCacheKey(normalizedVersionKey)
      const completedSnapshot = this.completedSnapshots.get(cacheKey)

      if (completedSnapshot) {
        return Promise.resolve(completedSnapshot)
      }

      const inFlightBuild = this.inFlightBuilds.get(cacheKey)
      if (inFlightBuild) {
        return inFlightBuild
      }

      // builder 放入微任务执行，确保同步抛错也遵守 Promise 失败契约，并让并发调用先看到 in-flight。
      const buildPromise = Promise.resolve()
        .then(() => builder(normalizedVersionKey))
        .then((snapshot) => this.publishSnapshot(normalizedVersionKey, snapshot))
        .finally(() => {
          this.inFlightBuilds.delete(cacheKey)
        })

      this.inFlightBuilds.set(cacheKey, buildPromise)
      return buildPromise
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * 淘汰一个已经发布的 WorkAssembly 版本。
   *
   * 该操作只处理完整发布的快照，不会尝试取消正在构建的事务。调用方应在新版本已经
   * 原子提交后再淘汰旧版本，保证切换失败时旧场景与 Worker 运行时仍可继续使用。
   *
   * @param versionKey 需要淘汰的仓库与提交版本。
   * @returns 找到并释放目标快照时返回 true；版本未发布时返回 false。
   */
  public evict(versionKey: PuppyCAD.WorkAssemblyVersionKey): boolean {
    this.assertAvailable()
    const normalizedVersionKey = this.normalizeVersionKey(versionKey)
    const cacheKey = this.createCacheKey(normalizedVersionKey)
    const snapshot = this.completedSnapshots.get(cacheKey)

    if (!snapshot) {
      return false
    }

    // 先移除缓存槽位再释放资源；即使 dispose 抛错，也不能让已失效快照再次被命中。
    this.completedSnapshots.delete(cacheKey)
    snapshot.dispose()
    return true
  }

  /**
   * 从注册表移出快照并把释放责任转交给调用方。
   *
   * 活动版本会话需要在快照释放失败时继续持有它，以便后续重试。该入口只改变注册表
   * 所有权，不调用 `dispose()`，避免注册表删除槽位后丢失失败快照。
   *
   * @param versionKey 需要移出的仓库与提交版本。
   * @returns 已移出的快照；版本未发布时返回 null。
   */
  public take(versionKey: PuppyCAD.WorkAssemblyVersionKey): PuppyCAD.WorkAssemblySnapshot | null {
    this.assertAvailable()
    const normalizedVersionKey = this.normalizeVersionKey(versionKey)
    const cacheKey = this.createCacheKey(normalizedVersionKey)
    const snapshot = this.completedSnapshots.get(cacheKey) ?? null
    this.completedSnapshots.delete(cacheKey)
    return snapshot
  }

  /**
   * 原子发布新 revision 并移出旧快照，但不销毁旧快照拥有的复用对象。
   *
   * @param previousVersionKey 当前活动 revision 版本。
   * @param nextSnapshot 已完成校验和构建的新 revision。
   * @returns 被替换的旧快照；未命中时返回 null。
   */
  public replace(
    previousVersionKey: PuppyCAD.WorkAssemblyVersionKey,
    nextSnapshot: PuppyCAD.WorkAssemblySnapshot,
  ): PuppyCAD.WorkAssemblySnapshot | null {
    this.assertAvailable()
    const normalizedNextVersionKey = this.normalizeVersionKey(nextSnapshot.versionKey)
    this.assertSnapshotVersion(normalizedNextVersionKey, nextSnapshot)
    const previousCacheKey = this.createCacheKey(this.normalizeVersionKey(previousVersionKey))
    const nextCacheKey = this.createCacheKey(normalizedNextVersionKey)
    const previousSnapshot = this.completedSnapshots.get(previousCacheKey) ?? null

    this.completedSnapshots.set(nextCacheKey, nextSnapshot)
    if (previousCacheKey !== nextCacheKey) {
      this.completedSnapshots.delete(previousCacheKey)
    }
    return previousSnapshot
  }

  /**
   * 销毁当前 Editor 会话拥有的全部 WorkAssembly 快照。
   *
   * 已发布快照会且只会被释放一次。尚未结束的 builder 不能被注册表强制取消；它在完成时
   * 会检测到会话已经销毁，立即释放刚构建的快照并拒绝交付。
   */
  public dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    const snapshots = [...new Set(this.completedSnapshots.values())]
    this.completedSnapshots.clear()
    const errors: unknown[] = []

    for (const snapshot of snapshots) {
      try {
        snapshot.dispose()
      } catch (error) {
        // 一个快照释放失败不能阻止其它 GPU/拓扑资源继续释放，最终统一报告全部异常。
        errors.push(error)
      }
    }

    if (errors.length > 0) {
      throw new AggregateError(errors, 'WorkAssembly 版本注册表销毁失败')
    }
  }

  /**
   * 原子发布一个完整构建结果。
   *
   * @param expectedVersionKey 调用 resolve 时已归一化的目标版本。
   * @param snapshot builder 返回的完整快照。
   * @returns 已成功发布的同一快照。
   */
  private publishSnapshot(
    expectedVersionKey: PuppyCAD.WorkAssemblyVersionKey,
    snapshot: PuppyCAD.WorkAssemblySnapshot,
  ): PuppyCAD.WorkAssemblySnapshot {
    try {
      this.assertSnapshotVersion(expectedVersionKey, snapshot)
      this.assertAvailable()
      this.completedSnapshots.set(this.createCacheKey(expectedVersionKey), snapshot)
      return snapshot
    } catch (error) {
      // 校验失败或会话中途销毁时，builder 已经交付所有权，必须在此处回收完整快照。
      snapshot.dispose()
      throw error
    }
  }

  /**
   * 校验 builder 没有把其它仓库或提交的快照错误发布到当前版本槽位。
   *
   * @param expectedVersionKey resolve 请求的目标版本。
   * @param snapshot builder 返回的快照。
   */
  private assertSnapshotVersion(
    expectedVersionKey: PuppyCAD.WorkAssemblyVersionKey,
    snapshot: PuppyCAD.WorkAssemblySnapshot,
  ): void {
    const actualVersionKey = this.normalizeVersionKey(snapshot.versionKey)

    if (this.createCacheKey(expectedVersionKey) !== this.createCacheKey(actualVersionKey)) {
      throw new Error(
        'WorkAssembly 构建结果版本不匹配：'
        + `expectedRepositoryId=${expectedVersionKey.repositoryId}, `
        + `expectedCommitId=${expectedVersionKey.commitId}, `
        + `actualRepositoryId=${actualVersionKey.repositoryId}, `
        + `actualCommitId=${actualVersionKey.commitId}`,
      )
    }
  }

  /**
   * 归一化并验证后端版本字段。
   *
   * @param versionKey 外层加载快照提供的版本键。
   * @returns 去除首尾空白后的不可变版本键。
   */
  private normalizeVersionKey(
    versionKey: PuppyCAD.WorkAssemblyVersionKey,
  ): PuppyCAD.WorkAssemblyVersionKey {
    const repositoryId = versionKey.repositoryId.trim()
    const commitId = versionKey.commitId.trim()

    if (!repositoryId || !commitId) {
      throw new Error(
        'WorkAssembly 版本键无效：'
        + `repositoryId=${repositoryId || '<empty>'}, commitId=${commitId || '<empty>'}`,
      )
    }

    return Object.freeze({ repositoryId, commitId })
  }

  /**
   * 生成不会因普通字符串分隔符发生碰撞的内部缓存键。
   *
   * @param versionKey 已归一化的版本键。
   * @returns 注册表内部 Map 使用的稳定字符串。
   */
  private createCacheKey(versionKey: PuppyCAD.WorkAssemblyVersionKey): string {
    return JSON.stringify([versionKey.repositoryId, versionKey.commitId])
  }

  /** 注册表销毁后禁止继续读取或启动新构建。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error('WorkAssembly 版本注册表已销毁')
    }
  }
}
